import qs from 'query-string';
import { withWindow } from './withWindow';

export const isValidUrl = (url: Maybe<string>): url is string => {
  if (typeof url !== 'string') {
    return false;
  }
  const res = url.match(/(http(s)?:\/\/.)/gi);
  return res !== null;
};

export const isValidUrlAndDomain = (url: Maybe<string>): url is string => {
  if (typeof url !== 'string') {
    return false;
  }
  const res = url.match(/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z0-9]{2,}(:[0-9]{1,5})?(\/.*)?$/gi);
  return res !== null;
};

export const getDomainNameFromProductExternalUrl = (productExternalUrl: Maybe<string>) => {
  if (!productExternalUrl || !isValidUrl(productExternalUrl)) {
    return undefined;
  }
  const domain = new URL(productExternalUrl || '');
  const siteName = domain.hostname.replace('www.', '');
  return siteName.charAt(0).toUpperCase() + siteName.slice(1);
};

interface KeyValue {
  [key: string]: string | undefined;
}

// Legacy manual parser — prefer URLSearchParams (via buildQueryString) for new code.
export const getURLParams = <ParamsKeyValues extends KeyValue>(search: string): ParamsKeyValues | KeyValue => {
  const paramValue = search
    .substring(1)
    .split('&')
    .reduce((params: KeyValue, newString) => {
      const kv = newString.split('=');
      if (kv[0] && kv[1]) {
        params[kv[0].replace(/\s+/g, '')] = kv[1].trim();
      }
      return params;
    }, {});
  return paramValue;
};

/*
  A function that extract URL from given string. If there is no matching URL, then it will return the given string itself.
  @param {string} text - The text from which the URL to be extracted
  @returns {string} - The extracted URL or the given text itself if no valid URL found
*/
export const extractURLFromText = (text: string) => {
  const urlRegex = /(?:https?:\/\/)?[\w.-]+\.[a-zA-Z]{2,}(\/\S*)?/gi;
  const matches = text.match(urlRegex);
  return matches ? matches[0] : text;
};

type HttpProtocolOptions = {
  allowHttp: boolean;
};
const DEFAULT_HTTP_PROTOCOL_OPTIONS: HttpProtocolOptions = { allowHttp: true };

export const isUrlWithHttpProtocol = (url: string, options: HttpProtocolOptions = DEFAULT_HTTP_PROTOCOL_OPTIONS) => {
  if (!url) {
    return false;
  }
  const urlLowercase = url.toLowerCase();
  if (urlLowercase.startsWith('https://')) {
    return true;
  }
  return options.allowHttp && urlLowercase.startsWith('http://');
};

/*
  To normalize url and make it valid url
*/
export const ensureHttpsPrefix = (url: string, options: HttpProtocolOptions = DEFAULT_HTTP_PROTOCOL_OPTIONS) => {
  if (!isUrlWithHttpProtocol(url, options)) {
    return `https://${url}`;
  }

  return url;
};

/**
 * Merges provided query parameters into a target URL, preserving existing parameters
 * and the fragment identifier. New query parameters override existing ones with the same key.
 *
 * @template Params - A map of query param names to their inferred types.
 *
 * @param {string} targetUrl - The URL to merge query parameters into.
 * @param {Partial<Params>} queryParams - Parsed query parameters to merge.
 *   Values can be strings, numbers, or arrays depending on accessor used.
 *
 * @returns {string} The final URL with merged query parameters and any original hash.
 *
 */
export const mergeQueryParamsToUrl = <Params extends Record<string, string | number | string[]>>(targetUrl: string, queryParams: Partial<Params>): string => {
  const {
    url: baseUrl,
    query: targetQuery,
    fragmentIdentifier,
  } = qs.parseUrl(targetUrl, {
    parseFragmentIdentifier: true,
    arrayFormat: 'comma',
  });

  const mergedQuery = { ...targetQuery, ...queryParams };

  return qs.stringifyUrl(
    {
      url: baseUrl,
      query: mergedQuery,
      fragmentIdentifier,
    },
    {
      skipEmptyString: true,
      skipNull: true,
      arrayFormat: 'comma',
    },
  );
};

/**
 * Returns true only for valid https URLs (uses the URL constructor for strict parsing).
 * Use this when you need to validate a redirect URL that must be https.
 */
export const isValidHttpsUrl = (urlString: string): boolean => {
  try {
    const parsed = new URL(urlString);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Validates that a value is a public web URL (http/https with a real domain).
 * Uses the browser's built-in URL parser (WHATWG standard) — more lenient than
 * Yup's `.url()` regex, so it accepts square brackets, unicode, etc.
 */
export const isPublicWebUrl = (value: string | null | undefined): boolean => {
  if (!value || !value.trim()) return false;

  try {
    const parsed = new URL(value.trim());
    const { protocol, hostname } = parsed;
    return ['http:', 'https:'].includes(protocol) && hostname.includes('.') && !hostname.startsWith('.') && !hostname.endsWith('.') && !hostname.includes('..');
  } catch {
    return false;
  }
};

/** For optional URL fields — returns true for empty/null, validates non-empty values. */
export const isOptionalPublicWebUrl = (value: string | null | undefined): boolean => !value?.trim() || isPublicWebUrl(value);

/**
 * Gets the absolute URL from a pathname.
 *
 * @param pathname - The pathname to get the absolute URL from.
 * @returns The absolute URL.
 *
 * @example
 * getAbsoluteUrlFromPathname("/settings");
 * // => "https://current-domain.com/settings"
 *
 * @example
 * getAbsoluteUrlFromPathname("/");
 * // => "https://current-domain.com/"
 *
 * @example
 * getAbsoluteUrlFromPathname("/docs/page?id=10");
 * // => "https://current-domain.com/docs/page?id=10"
 */
export const getAbsoluteUrlFromPathname = (pathname: string) => {
  return withWindow(global => `${global.location.origin}${pathname}`, pathname);
};

/**
 * Builds a query string from a record of key-value pairs using URLSearchParams.
 *
 * URLSearchParams is supported in all browsers we target — IE 11 is long gone.
 * This is the preferred way to construct query strings in joy-web.
 * Use this instead of manual string concatenation.
 *
 * Returns a string starting with `?` when there are params, or an empty string
 * when all values are nullish / the record is empty.
 *
 * @param params - Key-value pairs for query params. Values are coerced to strings.
 *   `null` and `undefined` values are omitted.
 *
 * @example
 * buildQueryString({ draftType: 'generic', fromDashboard: 'true' });
 * // => "?draftType=generic&fromDashboard=true"
 *
 * @example
 * buildQueryString({ a: null, b: undefined });
 * // => ""
 *
 * @example
 * // Compose with a pathname for new-tab opens:
 * withWindow(w => w.open(`/${handle}/edit/email/create${buildQueryString({ draftType: 'generic' })}`, '_blank', 'noopener'));
 */
export const buildQueryString = (params: Readonly<Record<string, string | number | boolean | null | undefined>>): string => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null) {
      searchParams.set(key, String(value));
    }
  }

  const query = searchParams.toString();
  return query ? `?${query}` : '';
};
